import { alpha, createTheme } from "@mui/material/styles";
import { getLanguageSetting } from "../localSettings/utils";
import * as datePickerLocales from "@mui/x-date-pickers/locales";
import * as coreLocales from "@mui/material/locale";
import * as dataGridLocales from "@mui/x-data-grid-pro/locales";
import { ButtonPropsColorOverrides, PaletteColor, PaletteColorOptions } from "@mui/material";

declare module "@mui/material/styles" {
	interface Palette {
		yellow: PaletteColor;
		green: PaletteColor;
		blue: PaletteColor;
		black: PaletteColor;
		brown: PaletteColor;
		chrome: {
			appBarBg: string;
			drawerBg: string;
			text: string;
			textSecondary: string;
			textMuted: string;
		};
		state: {
			initial: string;
			released: string;
			inProgress: string;
			completed: string;
			cancelled: string;
		};
	}

	interface PaletteOptions {
		yellow?: PaletteColorOptions;
		green?: PaletteColorOptions;
		blue?: PaletteColorOptions;
		black?: PaletteColor;
		brown?: PaletteColor;
		chrome?: {
			appBarBg: string;
			drawerBg: string;
			text: string;
			textSecondary: string;
			textMuted: string;
		};
	}
}

const disabledTextColor = alpha("#000000", 0.5);

const disabledInputStyle = {
	WebkitTextFillColor: disabledTextColor,
} as const;

const userLang = getLanguageSetting();
const themeInitial = createTheme(
	{
		palette: {
			primary: {
				main: "#2f6b62",
			},
			secondary: {
				main: "#f9b315",
			},
			error: {
				main: "#c73737",
			},
			warning: {
				main: "#f9b315",
			},
			success: {
				main: "#4caf50",
			},
			info: {
				main: "#2196f3",
			},
			background: {
				default: "#f8f9fa",
				paper: "#ffffff",
			},
		},
		shape: {
			borderRadius: 6,
		},
		typography: {
			fontFamily: [
				"-apple-system",
				"BlinkMacSystemFont",
				'"Segoe UI"',
				"Roboto",
				'"Helvetica Neue"',
				"Arial",
				'"Noto Sans"',
				'"Liberation Sans"',
				"sans-serif",
				'"Apple Color Emoji"',
				'"Segoe UI Emoji"',
				'"Segoe UI Symbol"',
				'"Noto Color Emoji"',
			].join(","),
			h1: { fontWeight: 600, letterSpacing: "-0.01em" },
			h2: { fontWeight: 600, letterSpacing: "-0.01em" },
			h3: { fontWeight: 600, letterSpacing: "-0.005em" },
			h4: { fontWeight: 600 },
			h5: { fontWeight: 600 },
			h6: { fontWeight: 600 },
			subtitle1: { fontWeight: 500 },
			subtitle2: { fontWeight: 500 },
			button: { fontWeight: 500, letterSpacing: "0.01em" },
			body1: {fontSize: "1rem"},
			body2: {fontSize: "0.875rem"}
		},
		transitions: {
			duration: {
				shortest: 120,
				shorter: 180,
				short: 220,
				standard: 260,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						WebkitFontSmoothing: "antialiased",
						MozOsxFontSmoothing: "grayscale",
					},
				},
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: "30px !important",
					},
				},
			},
		MuiTextField: {
			defaultProps: {
				size: "small",
			},
			styleOverrides: {
				root: {
					"& label.Mui-disabled": {
						color: disabledTextColor,
						opacity: 1,
					},
					"& input.Mui-disabled": disabledInputStyle,
					"& .MuiOutlinedInput-root": {
						transition: "border-color 0.2s ease, box-shadow 0.2s ease",
						"&.Mui-focused": {
							"& .MuiOutlinedInput-notchedOutline": {
								borderWidth: "1.5px",
							},
						},
					},
				},
			},
		},
		MuiAutocomplete: {
			defaultProps: {
				size: "small",
			},
			styleOverrides: {
				input: {
					"&.Mui-disabled": disabledInputStyle,
				},
			},
		},
			MuiButton: {
				styleOverrides: {
					root: {
						textTransform: "none",
						fontWeight: 500,
						borderRadius: 6,
						boxShadow: "none",
						"&:hover": {
							boxShadow: "none",
						},
					},
					contained: {
						"&:hover": {
							boxShadow: "0 1px 3px 0 rgba(0,0,0,0.12)",
						},
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						borderRadius: 6,
						transition: "background-color 0.15s ease",
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					rounded: {
						borderRadius: 8,
					},
					elevation1: {
						boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
					},
					elevation2: {
						boxShadow: "0 2px 6px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
					},
				},
			},
		MuiTooltip: {
			defaultProps: {
				enterDelay: 600,
				enterNextDelay: 400,
			},
			styleOverrides: {
				tooltip: {
					fontSize: "0.75rem",
					borderRadius: 4,
				},
			},
		},
			MuiDivider: {
				styleOverrides: {
					root: {
						borderColor: alpha("#000000", 0.08),
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						fontWeight: 500,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						borderRadius: 12,
					},
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						borderRadius: "8px !important",
						boxShadow: "0 4px 16px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.08) !important",
					},
					list: {
						padding: "4px",
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						borderRadius: 4,
						margin: "1px 0",
						fontSize: "0.875rem",
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: "none",
						fontWeight: 500,
						minHeight: 0,
					},
				},
			},
		},
	},
	// Localizations
	{
		en: coreLocales.enUS,
		fi: coreLocales.fiFI,
	}[userLang],
	{
		en: dataGridLocales.enUS,
		fi: dataGridLocales.fiFI,
	}[userLang],
	{
		en: datePickerLocales.enUS,
		fi: datePickerLocales.fiFI,
	}[userLang],
);

const yellowColor = themeInitial.palette.augmentColor({
	color: {
		main: "#f5cc00",
	},
	name: "yellow",
});

export const AAVO_MUI_THEME = createTheme(themeInitial, {
	palette: {
		yellow: themeInitial.palette.augmentColor({
			color: {
				main: "#f5cc00",
			},
			name: "yellow",
		}),
		green: themeInitial.palette.success,
		blue: themeInitial.palette.info,
		brown: themeInitial.palette.augmentColor({
			color: {
				main: "#7b5e37",
			},
			name: "brown",
		}),
		black: themeInitial.palette.augmentColor({
			color: {
				main: "#000000",
			},
			name: "black",
		}),
		chrome: {
			appBarBg: "#2f6b62",
			drawerBg: "#2f6b62",
			text: "rgba(232, 240, 238, 0.95)",
			textSecondary: "rgba(232, 240, 238, 0.72)",
			textMuted: "rgba(232, 240, 238, 0.6)",
		},
		state: {
			initial: themeInitial.palette.grey[600],
			released: themeInitial.palette.info.main,
			inProgress: yellowColor.main,
			completed: themeInitial.palette.success.dark,
			cancelled: themeInitial.palette.error.dark,
		},
	},
});

declare module "@mui/material/Button" {
	interface ButtonPropsColorOverrides {
		yellow: true;
		black: true;
	}
}

declare module "@mui/material/IconButton" {
	interface IconButtonPropsColorOverrides extends ButtonPropsColorOverrides {}
}
